import {
    FC,
    ReactElement,
    useEffect,
} from 'react'
import { NavLink, useNavigate, useParams } from 'react-router'

import { Button, Comment, InfoListItem, TimeTicker } from '@/components'
import { DateCard, EmployeesList } from '@/compositions'
import { isAfter } from '@/helpers/date'
import { convertDateToTime } from '@/helpers/DateTimeParser'
import { getDeviatedTimeShift, shouldShowEndTime } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { Shift } from '@/models'
import { ShiftStatus } from '@/models/Shift'
import { getShift, setShift, unregisterShift } from '@/redux/shift/shiftActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { transformLegacyBasicUserToBasicUser } from '@/services/UserService/transformLegacyBasicUserToBasicUser'

import './ShiftsDetailPage.scss'

const getShiftTime = (shift: Shift, showEndTime: boolean): string => {
    if (shift.end && showEndTime) {
        return `${convertDateToTime(shift.start)} - ${convertDateToTime(shift.end)}`
    }

    return convertDateToTime(shift.start)
}

// Shows the tracked time of a shift that already started
const renderTrackInfo = (shift: Shift): ReactElement | null => {
    const track = shift.tracks?.[0]

    if (! track) {
        return null
    }

    return (
        <>
            <InfoListItem label={trans('pages.shifts.clockedTime')}>
                {track.billableEnd
                    ? `${convertDateToTime(track.billableStart)} -
                ${convertDateToTime(track.billableEnd)}`
                    : <TimeTicker from={track.checkIn} />}
            </InfoListItem>

            {track.billableEnd && (
                <>
                    <InfoListItem label={trans('pages.shifts.workedTime')}>
                        {track.totalBillableTime}
                    </InfoListItem>
                    <InfoListItem label={trans('common.break')}>
                        {`${track.pause} ${trans('common.minutes')}`}
                    </InfoListItem>
                    <InfoListItem label={trans('common.meal')}>
                        {track.meal ? trans(`pages.tracks.${track.meal.toLowerCase()}`) : ''}
                    </InfoListItem>
                    <InfoListItem label={trans('common.satisfaction')}>
                        {track.happiness}
                    </InfoListItem>
                </>
            )}
        </>
    )
}

export const ShiftsDetailPage: FC = (): ReactElement | null => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const { shiftUuid } = useParams()
    const { shift } = useTypedSelector(state => state.shiftReducer)
    const { currentUser, settings } = useTypedSelector(state => state.userReducer)

    useEffect(() => {
        if (shiftUuid) {
            dispatch(getShift(shiftUuid))
        }

        return () => {
            dispatch(setShift(null))
        }
    }, [])

    if (! shift) {
        return null
    }

    const handleUnregisterClick = (): void => {
        dispatch(unregisterShift(shift.id, navigate))
    }

    const renderActions = (shiftStatus: ShiftStatus): ReactElement | null => {
        if (! isAfter(new Date(shift.start), new Date())) {
            return renderTrackInfo(shift)
        }

        switch (shiftStatus.getStatus()) {
            case 'offered':
            case 'open':
                return (
                    <NavLink
                        to={`/${trans('nav.shifts.link')}/${shift.id}/${trans('nav.shifts.register.link')}`}
                        className="shifts-detail__button"
                    >
                        {trans('common.subscribe')}
                    </NavLink>
                )
            case 'planned':
                return (
                    <NavLink
                        to={`/${trans('nav.shifts.link')}/${shiftUuid}/${trans('nav.shifts.exchange.link')}`}
                        className="shifts-detail__button"
                    >
                        {trans('pages.shifts.exchangeShift')}
                    </NavLink>
                )
            case 'registered':
                return (
                    <Button
                        text={trans('common.unsubscribe')}
                        onClick={handleUnregisterClick}
                        className="shifts-detail__button"
                    />
                )
            default:
                return null
        }
    }

    const shiftStatus = new ShiftStatus(shift)
    const department = shift.department?.name
    const group = shift.department?.group?.name
    const showEndTime = shouldShowEndTime(settings, shift.department?.id)
    const currentUserId = currentUser?.id
    const deviatedTime = getDeviatedTimeShift(shift, currentUserId, showEndTime)
    const userShiftPlanning = shift.shiftPlannings?.find(planning => planning.user?.id === currentUserId)
    const comments = userShiftPlanning?.comments || []
    const lastComment = comments.at(-1)
    const coworkers = (shift.plannedUsers || [])
        .filter(user => user.id !== currentUserId)
        .map(transformLegacyBasicUserToBasicUser)

    return (
        <div className="shifts-detail">
            <DateCard
                color={shiftStatus.getColor()}
                date={new Date(shift.start)}
                className="shifts-detail__header"
            />
            <ul className="shifts-detail__list">
                <InfoListItem label={trans('common.department')}>
                    {`${group ? `${group} - ` : ''}${department}`}
                </InfoListItem>
                {deviatedTime && (
                    <InfoListItem label={trans('pages.shifts.deviatedShiftTime')}>
                        {deviatedTime}
                    </InfoListItem>
                )}
                <InfoListItem label={deviatedTime ? trans('pages.shifts.originalShiftTime') : trans('common.shiftTime')}>
                    {getShiftTime(shift, showEndTime)}
                </InfoListItem>
            </ul>
            {lastComment && (
                <ul className="shifts-detail__list">
                    <InfoListItem label={trans('common.note')}>
                        <table>
                            <tbody>
                                <Comment comment={lastComment} />
                            </tbody>
                        </table>
                    </InfoListItem>
                </ul>
            )}
            <EmployeesList
                employees={coworkers}
                className="shifts-detail__list"
            />
            {renderActions(shiftStatus)}
        </div>
    )
}
